import { IOption } from "../data/AppInterface";
import { TUseTranslationTfn } from "../data/AppType";
import { BIT_VALUE } from "./AppEnumerations";
import {
  CREDIT_LEDGER_REASON_ENUM,
  CREDIT_TYPE_ENUM,
  GATEWAY_PAYMENT_STATUS_ENUM,
  ORDER_CONFIRMED_BY_ENUM,
  ORDER_DISPLAY_STATUS_ENUM,
  ORDER_KIND_ENUM,
  PACKAGE_CONFIG_KEY_ENUM,
  PLATFORM_ENUM,
  STOCK_VALUATION_METHOD_ENUM,
  SUBSCRIPTION_SOURCE_ENUM,
  VISIBILITY_TYPE_ENUM,
  WEBHOOK_EVENT_STATUS_ENUM,
} from "./PurchaseEnumerations";

export const CREDIT_BALANCE_MAX = 9999999;
export const TWO_DECIMALS_REGEX = /^\d+(\.\d{1,2})?$/;
export const PRICE_MAX = 9999999.99;
export const TAX_PERCENT_MAX = 100;
export const VALIDITY_DAYS_MAX = 3650;
export const PURCHASE_NAME_MAX_LENGTH = 100;
export const PACKAGE_DESCRIPTION_MAX_LENGTH = 500;
export const FEATURE_ANNOTATION_MAX_LENGTH = 50;
export const GRANT_NOTE_MAX_LENGTH = 300;
export const FOOTER_NOTE_MAX_LENGTH = 250;
export const MINIMUM_TOTAL_PAISE = 100;
export const CONTACT_TO_SALES_CONFIG_VALUE = "-1";
export const GSTIN_REGEX =
  /^(0[1-9]|1[0-9]|2[0-9]|3[0-7])[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const SAC_CODE_REGEX = /^[0-9]{4,8}$/;
export const ZIP_CODE_REGEX = /^[A-Za-z0-9 -]{3,10}$/;
export const EXPIRY_WARNING_DAYS = 10;

export const CHIP_CLASS = {
  success: "bg-success-muted text-success",
  warning: "bg-warning-muted text-warning",
  danger: "bg-danger-muted text-danger",
  info: "bg-accent-muted text-accent",
  grey: "bg-inset text-muted",
};

const CREDIT_TYPE_ORDER: readonly CREDIT_TYPE_ENUM[] = [
  CREDIT_TYPE_ENUM.Whatsapp,
  CREDIT_TYPE_ENUM.EInvoice,
  CREDIT_TYPE_ENUM.EWayBill,
  CREDIT_TYPE_ENUM.Ai,
];

export const CREDIT_TYPE_LABEL_KEY: Record<CREDIT_TYPE_ENUM, string> = {
  [CREDIT_TYPE_ENUM.Whatsapp]: "whatsapp",
  [CREDIT_TYPE_ENUM.EInvoice]: "credit_type_e_invoice",
  [CREDIT_TYPE_ENUM.EWayBill]: "credit_type_e_way_bill",
  [CREDIT_TYPE_ENUM.Ai]: "credit_type_ai",
};

export const CREDIT_TYPE_CHIP_CLASS: Record<CREDIT_TYPE_ENUM, string> = {
  [CREDIT_TYPE_ENUM.Whatsapp]: CHIP_CLASS.success,
  [CREDIT_TYPE_ENUM.EInvoice]: CHIP_CLASS.info,
  [CREDIT_TYPE_ENUM.EWayBill]: CHIP_CLASS.warning,
  [CREDIT_TYPE_ENUM.Ai]: CHIP_CLASS.grey,
};

export const VISIBILITY_TYPE_LABEL_KEY: Record<VISIBILITY_TYPE_ENUM, string> = {
  [VISIBILITY_TYPE_ENUM.Public]: "visibility_public",
  [VISIBILITY_TYPE_ENUM.ContactToSale]: "visibility_contact_to_sale",
  [VISIBILITY_TYPE_ENUM.Private]: "visibility_private",
  [VISIBILITY_TYPE_ENUM.FreeTrial]: "free_trial",
};

export const VISIBILITY_TYPE_CHIP_CLASS: Record<VISIBILITY_TYPE_ENUM, string> =
  {
    [VISIBILITY_TYPE_ENUM.Public]: CHIP_CLASS.success,
    [VISIBILITY_TYPE_ENUM.ContactToSale]: CHIP_CLASS.info,
    [VISIBILITY_TYPE_ENUM.Private]: CHIP_CLASS.grey,
    [VISIBILITY_TYPE_ENUM.FreeTrial]: CHIP_CLASS.warning,
  };

export const LEDGER_REASON_LABEL_KEY: Record<
  CREDIT_LEDGER_REASON_ENUM,
  string
> = {
  [CREDIT_LEDGER_REASON_ENUM.PackageIncluded]: "ledger_reason_package_included",
  [CREDIT_LEDGER_REASON_ENUM.PlanPurchase]: "ledger_reason_plan_purchase",
  [CREDIT_LEDGER_REASON_ENUM.AdminGrant]: "ledger_reason_admin_grant",
  [CREDIT_LEDGER_REASON_ENUM.Used]: "ledger_reason_used",
  [CREDIT_LEDGER_REASON_ENUM.Returned]: "ledger_reason_returned",
};

export const LEDGER_REASON_CHIP_CLASS: Record<
  CREDIT_LEDGER_REASON_ENUM,
  string
> = {
  [CREDIT_LEDGER_REASON_ENUM.PackageIncluded]: CHIP_CLASS.info,
  [CREDIT_LEDGER_REASON_ENUM.PlanPurchase]: CHIP_CLASS.success,
  [CREDIT_LEDGER_REASON_ENUM.AdminGrant]: CHIP_CLASS.warning,
  [CREDIT_LEDGER_REASON_ENUM.Used]: CHIP_CLASS.danger,
  [CREDIT_LEDGER_REASON_ENUM.Returned]: CHIP_CLASS.grey,
};

export const SUBSCRIPTION_SOURCE_LABEL_KEY: Record<
  SUBSCRIPTION_SOURCE_ENUM,
  string
> = {
  [SUBSCRIPTION_SOURCE_ENUM.Purchase]: "source_purchase",
  [SUBSCRIPTION_SOURCE_ENUM.AdminAssignment]: "source_admin_assignment",
  [SUBSCRIPTION_SOURCE_ENUM.FreeTrial]: "free_trial",
};

export const SUBSCRIPTION_SOURCE_CHIP_CLASS: Record<
  SUBSCRIPTION_SOURCE_ENUM,
  string
> = {
  [SUBSCRIPTION_SOURCE_ENUM.Purchase]: CHIP_CLASS.success,
  [SUBSCRIPTION_SOURCE_ENUM.AdminAssignment]: CHIP_CLASS.grey,
  [SUBSCRIPTION_SOURCE_ENUM.FreeTrial]: CHIP_CLASS.warning,
};

export const ORDER_KIND_LABEL_KEY: Record<ORDER_KIND_ENUM, string> = {
  [ORDER_KIND_ENUM.SubscriptionPackage]: "package",
  [ORDER_KIND_ENUM.CreditPlan]: "order_kind_credit_plan",
};

export const ORDER_CONFIRMED_BY_LABEL_KEY: Record<
  ORDER_CONFIRMED_BY_ENUM,
  string
> = {
  [ORDER_CONFIRMED_BY_ENUM.Verify]: "confirmed_by_verify",
  [ORDER_CONFIRMED_BY_ENUM.Poll]: "confirmed_by_poll",
  [ORDER_CONFIRMED_BY_ENUM.Webhook]: "confirmed_by_webhook",
  [ORDER_CONFIRMED_BY_ENUM.Reconciliation]: "confirmed_by_reconciliation",
};

export const ORDER_STATUS_LABEL_KEY: Record<ORDER_DISPLAY_STATUS_ENUM, string> =
  {
    [ORDER_DISPLAY_STATUS_ENUM.All]: "all",
    [ORDER_DISPLAY_STATUS_ENUM.Paid]: "order_status_paid",
    [ORDER_DISPLAY_STATUS_ENUM.Pending]: "order_status_pending",
    [ORDER_DISPLAY_STATUS_ENUM.Failed]: "order_status_failed",
    [ORDER_DISPLAY_STATUS_ENUM.Abandoned]: "order_status_abandoned",
    [ORDER_DISPLAY_STATUS_ENUM.Duplicate]: "duplicate",
    [ORDER_DISPLAY_STATUS_ENUM.Closed]: "order_status_closed",
  };

export const ORDER_STATUS_CHIP_CLASS: Record<
  ORDER_DISPLAY_STATUS_ENUM,
  string
> = {
  [ORDER_DISPLAY_STATUS_ENUM.All]: CHIP_CLASS.grey,
  [ORDER_DISPLAY_STATUS_ENUM.Paid]: CHIP_CLASS.success,
  [ORDER_DISPLAY_STATUS_ENUM.Pending]: CHIP_CLASS.warning,
  [ORDER_DISPLAY_STATUS_ENUM.Failed]: CHIP_CLASS.danger,
  [ORDER_DISPLAY_STATUS_ENUM.Abandoned]: CHIP_CLASS.grey,
  [ORDER_DISPLAY_STATUS_ENUM.Duplicate]: CHIP_CLASS.warning,
  [ORDER_DISPLAY_STATUS_ENUM.Closed]: CHIP_CLASS.grey,
};

export const GATEWAY_PAYMENT_CHIP_CLASS: Record<
  GATEWAY_PAYMENT_STATUS_ENUM,
  string
> = {
  [GATEWAY_PAYMENT_STATUS_ENUM.Created]: CHIP_CLASS.grey,
  [GATEWAY_PAYMENT_STATUS_ENUM.Authorized]: CHIP_CLASS.warning,
  [GATEWAY_PAYMENT_STATUS_ENUM.Captured]: CHIP_CLASS.success,
  [GATEWAY_PAYMENT_STATUS_ENUM.Refunded]: CHIP_CLASS.info,
  [GATEWAY_PAYMENT_STATUS_ENUM.Failed]: CHIP_CLASS.danger,
};

export const WEBHOOK_EVENT_CHIP_CLASS: Record<
  WEBHOOK_EVENT_STATUS_ENUM,
  string
> = {
  [WEBHOOK_EVENT_STATUS_ENUM.Received]: CHIP_CLASS.warning,
  [WEBHOOK_EVENT_STATUS_ENUM.Processed]: CHIP_CLASS.success,
  [WEBHOOK_EVENT_STATUS_ENUM.Ignored]: CHIP_CLASS.grey,
  [WEBHOOK_EVENT_STATUS_ENUM.Failed]: CHIP_CLASS.danger,
};

export const PACKAGE_LIMIT_KEYS: readonly PACKAGE_CONFIG_KEY_ENUM[] = [
  PACKAGE_CONFIG_KEY_ENUM.NumberOfUsers,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfRoles,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfBranches,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfFirms,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfGodowns,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfOcrScans,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfAiBotRequests,
];

export const PACKAGE_CREDIT_KEYS: readonly PACKAGE_CONFIG_KEY_ENUM[] = [
  PACKAGE_CONFIG_KEY_ENUM.NumberOfWhatsappCredits,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfEInvoiceCredits,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfEWayBillCredits,
  PACKAGE_CONFIG_KEY_ENUM.NumberOfAiCredits,
];

export const PACKAGE_CONFIG_LABEL_KEY: Record<PACKAGE_CONFIG_KEY_ENUM, string> =
  {
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfUsers]: "limit_users",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfRoles]: "roles",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfBranches]: "limit_branches",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfFirms]: "limit_firms",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfGodowns]: "limit_godowns",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfOcrScans]: "limit_ocr_scans",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfAiBotRequests]: "limit_ai_bot_requests",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfWhatsappCredits]: "whatsapp_credits",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfEInvoiceCredits]: "e_invoice_credits",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfEWayBillCredits]: "e_way_bill_credits",
    [PACKAGE_CONFIG_KEY_ENUM.NumberOfAiCredits]: "ai_credits",
    [PACKAGE_CONFIG_KEY_ENUM.AllowedStockValuationMethods]:
      "limit_stock_valuation",
  };

export const STOCK_VALUATION_LABEL_KEY: Record<
  STOCK_VALUATION_METHOD_ENUM,
  string
> = {
  [STOCK_VALUATION_METHOD_ENUM.Fifo]: "stock_valuation_fifo",
  [STOCK_VALUATION_METHOD_ENUM.Lifo]: "stock_valuation_lifo",
  [STOCK_VALUATION_METHOD_ENUM.WeightedAverage]:
    "stock_valuation_weighted_average",
};

const hasDesktopPlatform = (platforms: readonly string[]): boolean =>
  platforms.includes(PLATFORM_ENUM.Desktop);

export const getPlanTypeLabel = (
  t: TUseTranslationTfn,
  platforms: readonly string[],
): string =>
  hasDesktopPlatform(platforms)
    ? t("plan_type_mobile_desktop")
    : t("plan_type_mobile");

export const getVisibilityTypeOptions = (
  t: TUseTranslationTfn,
  includeFreeTrial: boolean,
): IOption<string>[] =>
  Object.values(VISIBILITY_TYPE_ENUM)
    .filter(
      (type) => includeFreeTrial || type !== VISIBILITY_TYPE_ENUM.FreeTrial,
    )
    .map((type) => ({
      data_value: type,
      display_value: t(VISIBILITY_TYPE_LABEL_KEY[type]),
    }));

export const getCreditTypeOptions = (
  t: TUseTranslationTfn,
): IOption<string>[] =>
  CREDIT_TYPE_ORDER.map((type) => ({
    data_value: type,
    display_value: t(CREDIT_TYPE_LABEL_KEY[type]),
  }));

export const getOrderStatusOptions = (
  t: TUseTranslationTfn,
): IOption<string>[] =>
  Object.values(ORDER_DISPLAY_STATUS_ENUM).map((status) => ({
    data_value: status,
    display_value: t(ORDER_STATUS_LABEL_KEY[status]),
  }));

export const getOrderKindOptions = (t: TUseTranslationTfn): IOption<string>[] =>
  Object.values(ORDER_KIND_ENUM).map((kind) => ({
    data_value: kind,
    display_value: t(ORDER_KIND_LABEL_KEY[kind]),
  }));

export const getSourceOptions = (t: TUseTranslationTfn): IOption<string>[] =>
  Object.values(SUBSCRIPTION_SOURCE_ENUM).map((source) => ({
    data_value: source,
    display_value: t(SUBSCRIPTION_SOURCE_LABEL_KEY[source]),
  }));

export const getActiveStatusOptions = (
  t: TUseTranslationTfn,
): IOption<string>[] => [
  { data_value: BIT_VALUE.One, display_value: t("active") },
  { data_value: BIT_VALUE.Zero, display_value: t("inactive") },
];

export const getPlatformFilterOptions = (
  t: TUseTranslationTfn,
): IOption<string>[] => [
  { data_value: BIT_VALUE.Zero, display_value: t("plan_type_mobile") },
  { data_value: BIT_VALUE.One, display_value: t("plan_type_mobile_desktop") },
];
